import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Terminal, Plus, RefreshCw, X, Save, Trash2, ArrowRight, ChevronDown,
  TrendingUp, DollarSign, Target, Briefcase, Edit3, Mail, Calendar, Award,
} from "lucide-react";

// Phase 25.2 — First-5-Customers Sales Pipeline Tracker.
// Bloomberg-styled CRM-lite for the founder. Auto-imports leads from
// existing pipelines and lets you progress them through 7 stages.

const STAGES = ["NEW", "QUALIFIED", "DEMO_BOOKED", "IN_PILOT", "NEGOTIATING", "WON", "LOST"];
const STAGE_COLORS = {
  NEW: { border: "border-zinc-700", text: "text-zinc-400", bg: "bg-zinc-900/40", chip: "border-zinc-700 text-zinc-400" },
  QUALIFIED: { border: "border-[#FFB000]/50", text: "text-[#FFB000]", bg: "bg-[#FFB000]/[0.04]", chip: "border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/10" },
  DEMO_BOOKED: { border: "border-[#FFB000]/70", text: "text-[#FFB000]", bg: "bg-[#FFB000]/[0.06]", chip: "border-[#FFB000]/60 text-[#FFB000] bg-[#FFB000]/10" },
  IN_PILOT: { border: "border-[#00E676]/40", text: "text-[#00E676]", bg: "bg-[#00E676]/[0.04]", chip: "border-[#00E676]/40 text-[#00E676] bg-[#00E676]/10" },
  NEGOTIATING: { border: "border-[#00E676]/60", text: "text-[#00E676]", bg: "bg-[#00E676]/[0.07]", chip: "border-[#00E676]/60 text-[#00E676] bg-[#00E676]/10" },
  WON: { border: "border-[#00E676]", text: "text-[#00E676]", bg: "bg-[#00E676]/10", chip: "border-[#00E676] text-[#00E676] bg-[#00E676]/20" },
  LOST: { border: "border-[#FF6B47]/30", text: "text-[#FF6B47]", bg: "bg-[#FF6B47]/[0.04]", chip: "border-[#FF6B47]/40 text-[#FF6B47] bg-[#FF6B47]/10" },
};

const fmt$ = (n) => {
  if (!Number.isFinite(n) || n === 0) return "—";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

export default function SalesTracker() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // row being edited (or {} for new)
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("kanban");

  const load = async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        api.get("/admin/sales/pipeline"),
        api.get("/admin/sales/pipeline/summary"),
      ]);
      setRows(list.data?.rows || []);
      setSummary(sum.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sync = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/sales/pipeline/sync");
      toast.success(`Synced — ${r.data.inserted} new leads, ${r.data.skipped_existing} already in pipeline`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.company && !editing.name) {
      toast.error("Need at least a name or company");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: editing.name || "",
        email: editing.email || null,
        company: editing.company || "",
        role: editing.role || "",
        source: editing.source || "manual",
        source_id: editing.source_id || null,
        stage: editing.stage || "NEW",
        value_usd_per_month: editing.value_usd_per_month != null ? Number(editing.value_usd_per_month) : null,
        value_one_off_usd: editing.value_one_off_usd != null ? Number(editing.value_one_off_usd) : null,
        next_action: editing.next_action || "",
        next_action_at: editing.next_action_at || null,
        notes: editing.notes || "",
        lost_reason: editing.lost_reason || null,
        lead_score: editing.lead_score != null ? Number(editing.lead_score) : null,
      };
      if (editing.id) {
        await api.patch(`/admin/sales/pipeline/${editing.id}`, payload);
        toast.success("Row updated");
      } else {
        await api.post("/admin/sales/pipeline", payload);
        toast.success("Row created");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this row? Cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/sales/pipeline/${id}`);
      toast.success("Row deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const moveStage = async (row, newStage) => {
    if (row.stage === newStage) return;
    try {
      await api.patch(`/admin/sales/pipeline/${row.id}`, { ...row, stage: newStage });
      toast.success(`${row.company || row.name} → ${newStage}`);
      await load();
    } catch (e) {
      toast.error("Stage update failed");
    }
  };

  const convertToCaseStudy = async (row) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/sales/pipeline/${row.id}/convert-to-case-study`);
      const draft = r.data?.draft;
      if (!draft) {
        toast.error("Conversion returned no draft");
        return;
      }
      const matched = r.data?.matched_workspace;
      toast.success(
        matched
          ? `Draft built from ${row.company}'s live workspace data — review + publish.`
          : `Draft created for ${row.company} (no matching workspace — fill metrics manually).`,
        { duration: 6000 }
      );
      // Park the draft in sessionStorage so the case studies page can pick it up
      sessionStorage.setItem("dara_pending_case_study_draft", JSON.stringify(draft));
      setEditing(null);
      nav("/app/admin/case-studies?from_won=1");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversion failed");
    } finally {
      setBusy(false);
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    STAGES.forEach(s => { g[s] = []; });
    for (const r of rows) {
      const s = r.stage || "NEW";
      if (!g[s]) g[s] = [];
      g[s].push(r);
    }
    return g;
  }, [rows]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="sales-tracker-page">
      {/* Header */}
      <header className="border-b border-zinc-900 sticky top-0 bg-black/95 backdrop-blur z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/app/today" className="flex items-center gap-2 group flex-shrink-0">
              <Terminal className="w-5 h-5 text-[#FFB000]" />
              <span className="font-heading font-black tracking-tight text-lg">DARA</span>
            </Link>
            <div className="hidden sm:block w-px h-6 bg-zinc-800" />
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">// ADMIN · SALES PIPELINE</div>
              <h1 className="font-heading font-black text-lg sm:text-xl tracking-tight">First 5 Customers</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setView(view === "kanban" ? "table" : "kanban")}
              data-testid="sales-view-toggle"
              className="border border-zinc-800 hover:border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300"
            >
              {view === "kanban" ? "TABLE" : "KANBAN"}
            </button>
            <button
              onClick={sync}
              disabled={busy}
              data-testid="sales-sync-btn"
              className="border border-zinc-800 hover:border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 inline-flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
              SYNC LEADS
            </button>
            <button
              onClick={() => setEditing({ stage: "NEW", source: "manual" })}
              data-testid="sales-add-btn"
              className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-4 py-2 hover:opacity-90 inline-flex items-center gap-2"
            >
              <Plus className="w-3.5 h-3.5" />
              ADD PROSPECT
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        {/* Summary KPIs */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="sales-summary">
            <SummaryKPI
              label="Won ARR"
              value={fmt$(summary.won_arr_usd)}
              sub={`MRR ${fmt$(summary.won_mrr_usd)}/mo`}
              accent="#00E676"
              icon={DollarSign}
              testid="sales-kpi-won-arr"
            />
            <SummaryKPI
              label="Pipeline ARR"
              value={fmt$(summary.pipeline_arr_usd)}
              sub={`MRR ${fmt$(summary.pipeline_mrr_usd)}/mo`}
              accent="#FFB000"
              icon={TrendingUp}
              testid="sales-kpi-pipeline-arr"
            />
            <SummaryKPI
              label="In Negotiation"
              value={`${summary.by_stage.NEGOTIATING || 0}`}
              sub={`MRR at risk ${fmt$(summary.at_risk_mrr_usd)}/mo`}
              accent="#FFB000"
              icon={Target}
              testid="sales-kpi-negotiating"
            />
            <SummaryKPI
              label="In Pilot"
              value={`${summary.by_stage.IN_PILOT || 0}`}
              sub="Live engagements"
              accent="#00E676"
              icon={Briefcase}
              testid="sales-kpi-pilot"
            />
            <SummaryKPI
              label="Total Prospects"
              value={`${summary.total_rows || 0}`}
              sub={`${summary.by_stage.NEW || 0} new · ${summary.by_stage.WON || 0} won`}
              accent="#FFFFFF"
              icon={Briefcase}
              testid="sales-kpi-total"
            />
          </div>
        )}

        {/* Body */}
        {loading && (
          <div className="text-center py-20 font-mono text-[11px] uppercase tracking-widest text-zinc-600">
            LOADING PIPELINE…
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="border border-zinc-900 bg-zinc-950/40 p-10 text-center">
            <Briefcase className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-lg">No prospects yet</h3>
            <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto leading-relaxed">
              Click <strong className="text-white">SYNC LEADS</strong> to auto-import from your industrial waitlist,
              founder guide signups, and industry leads. Or click <strong className="text-white">ADD PROSPECT</strong> to enter manually.
            </p>
          </div>
        )}

        {!loading && rows.length > 0 && view === "kanban" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3" data-testid="sales-kanban">
            {STAGES.map((stage) => (
              <KanbanColumn
                key={stage}
                stage={stage}
                rows={grouped[stage]}
                onEdit={setEditing}
                onMove={moveStage}
              />
            ))}
          </div>
        )}

        {!loading && rows.length > 0 && view === "table" && (
          <div className="border border-zinc-900 overflow-x-auto" data-testid="sales-table">
            <table className="w-full font-mono text-[11px]">
              <thead className="border-b border-zinc-900 bg-zinc-950">
                <tr className="text-zinc-500 uppercase tracking-widest text-[10px]">
                  <th className="text-left px-3 py-2.5">Company</th>
                  <th className="text-left px-3 py-2.5">Contact</th>
                  <th className="text-left px-3 py-2.5">Source</th>
                  <th className="text-left px-3 py-2.5">Stage</th>
                  <th className="text-right px-3 py-2.5">$/mo</th>
                  <th className="text-right px-3 py-2.5">$ one-off</th>
                  <th className="text-left px-3 py-2.5">Next action</th>
                  <th className="text-right px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <TableRow key={r.id} row={r} onEdit={setEditing} onDelete={remove} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit drawer */}
      {editing && (
        <EditDrawer
          row={editing}
          setRow={setEditing}
          onSave={save}
          onClose={() => setEditing(null)}
          onDelete={editing.id ? () => { remove(editing.id); setEditing(null); } : null}
          onConvertToCaseStudy={editing.id && editing.stage === "WON" ? () => convertToCaseStudy(editing) : null}
          busy={busy}
        />
      )}
    </div>
  );
}

function SummaryKPI({ label, value, sub, accent, icon: Icon, testid }) {
  return (
    <div className="border border-zinc-900 bg-zinc-950/40 p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: accent }} />}
      </div>
      <div className="font-heading font-black text-2xl tracking-tighter" style={{ color: accent }}>{value}</div>
      <div className="font-mono text-[9px] text-zinc-600 mt-1">{sub}</div>
    </div>
  );
}

function KanbanColumn({ stage, rows, onEdit, onMove }) {
  const c = STAGE_COLORS[stage];
  return (
    <div className={`border ${c.border} ${c.bg} flex flex-col min-h-[200px]`} data-testid={`sales-col-${stage}`}>
      <div className="px-3 py-2 border-b border-zinc-900/80 flex items-center justify-between sticky top-0 bg-black/80 backdrop-blur">
        <span className={`font-mono text-[10px] uppercase tracking-widest ${c.text} font-bold`}>
          {stage.replace("_", " ")}
        </span>
        <span className="font-mono text-[10px] text-zinc-600">{rows.length}</span>
      </div>
      <div className="flex-1 p-2 space-y-2">
        {rows.map((r) => (
          <KanbanCard key={r.id} row={r} onEdit={onEdit} onMove={onMove} />
        ))}
        {rows.length === 0 && (
          <div className="text-center py-6 font-mono text-[10px] text-zinc-700">empty</div>
        )}
      </div>
    </div>
  );
}

function KanbanCard({ row, onEdit, onMove }) {
  const nextStageIdx = STAGES.indexOf(row.stage) + 1;
  const canAdvance = nextStageIdx < STAGES.length - 1; // skip LOST as auto-advance
  return (
    <div
      onClick={() => onEdit(row)}
      data-testid={`sales-card-${row.id}`}
      className="border border-zinc-800 bg-black hover:border-zinc-700 p-3 cursor-pointer transition-colors group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-heading font-bold text-xs text-white truncate flex-1 min-w-0">
          {row.company || row.name || "(no name)"}
        </div>
        {row.lead_score != null && row.lead_score > 0 && (
          <span className={`font-mono text-[9px] px-1.5 py-px border ${row.lead_score >= 60 ? "border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/10" : "border-zinc-800 text-zinc-500"} flex-shrink-0`}>
            {row.lead_score}
          </span>
        )}
      </div>
      {(row.name && row.company) && <div className="font-mono text-[10px] text-zinc-500 mt-0.5 truncate">{row.name}</div>}
      {row.email && <div className="font-mono text-[10px] text-zinc-600 mt-0.5 truncate">{row.email}</div>}
      <div className="flex items-center justify-between mt-2 gap-2">
        <span className="font-mono text-[9px] text-zinc-700 uppercase tracking-widest truncate">
          {row.source.replace("_", " ")}
        </span>
        <div className="font-mono text-[10px] text-[#00E676] flex-shrink-0">
          {row.value_usd_per_month ? `${fmt$(row.value_usd_per_month)}/mo` : (row.value_one_off_usd ? fmt$(row.value_one_off_usd) : "")}
        </div>
      </div>
      {row.next_action && (
        <div className="flex items-start gap-1 mt-2 pt-2 border-t border-zinc-900">
          <ArrowRight className="w-3 h-3 text-[#FFB000] flex-shrink-0 mt-0.5" />
          <span className="font-mono text-[10px] text-zinc-300 line-clamp-2">{row.next_action}</span>
        </div>
      )}
      {canAdvance && (
        <button
          onClick={(e) => { e.stopPropagation(); onMove(row, STAGES[nextStageIdx]); }}
          data-testid={`sales-advance-${row.id}`}
          className="mt-2 w-full text-center font-mono text-[9px] uppercase tracking-widest text-zinc-600 hover:text-[#FFB000] py-1 border-t border-zinc-900 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          → {STAGES[nextStageIdx].replace("_", " ")}
        </button>
      )}
    </div>
  );
}

function TableRow({ row, onEdit, onDelete }) {
  const c = STAGE_COLORS[row.stage] || STAGE_COLORS.NEW;
  return (
    <tr
      className="border-b border-zinc-900 hover:bg-zinc-950/40 cursor-pointer"
      onClick={() => onEdit(row)}
      data-testid={`sales-row-${row.id}`}
    >
      <td className="px-3 py-2.5 text-white">{row.company || "—"}</td>
      <td className="px-3 py-2.5 text-zinc-400">
        <div>{row.name || "—"}</div>
        {row.email && <div className="text-[10px] text-zinc-600">{row.email}</div>}
      </td>
      <td className="px-3 py-2.5 text-zinc-500 uppercase tracking-widest text-[10px]">{row.source.replace("_", " ")}</td>
      <td className="px-3 py-2.5">
        <span className={`px-2 py-0.5 border ${c.chip} font-mono text-[9px] uppercase tracking-widest`}>
          {row.stage.replace("_", " ")}
        </span>
      </td>
      <td className="px-3 py-2.5 text-right text-[#00E676]">{row.value_usd_per_month ? fmt$(row.value_usd_per_month) : ""}</td>
      <td className="px-3 py-2.5 text-right text-zinc-400">{row.value_one_off_usd ? fmt$(row.value_one_off_usd) : ""}</td>
      <td className="px-3 py-2.5 text-zinc-300 max-w-xs truncate">{row.next_action || ""}</td>
      <td className="px-3 py-2.5 text-right">
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(row.id); }}
          className="text-zinc-700 hover:text-[#FF6B47] p-1"
          data-testid={`sales-delete-${row.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </td>
    </tr>
  );
}

function EditDrawer({ row, setRow, onSave, onClose, onDelete, onConvertToCaseStudy, busy }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="sales-edit-drawer"
    >
      <div
        className="w-full max-w-md bg-black border-l border-zinc-800 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-zinc-900 px-5 py-4 flex items-center justify-between sticky top-0 bg-black/95 backdrop-blur">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">// {row.id ? "EDIT" : "NEW"} PROSPECT</div>
            <div className="font-heading font-black text-base mt-0.5">{row.company || row.name || "Untitled"}</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="sales-edit-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Convert-to-case-study CTA — only shown when prospect is WON (Phase 25.2c) */}
        {onConvertToCaseStudy && (
          <div className="border-b border-zinc-900 px-5 py-4 bg-gradient-to-br from-[#00E676]/[0.06] to-zinc-950/40">
            <div className="flex items-start gap-3">
              <Award className="w-5 h-5 text-[#00E676] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#00E676] mb-1">
                  // WON · TURN INTO MARKETING COLLATERAL
                </div>
                <div className="font-heading font-bold text-sm">
                  Convert to Case Study
                </div>
                <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                  Auto-builds a draft using {row.company || "this customer"}'s live workspace data —
                  POS revenue, hedge programmes, DARA Briefing snapshots. Edit + publish in 60 sec.
                </p>
                <button
                  onClick={onConvertToCaseStudy}
                  disabled={busy}
                  data-testid="sales-convert-cs-btn"
                  className="mt-3 bg-[#00E676] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  <Award className="w-3 h-3" />
                  {busy ? "BUILDING…" : "BUILD DRAFT →"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="p-5 space-y-3">
          <Input label="COMPANY" value={row.company || ""} onChange={v => setRow({ ...row, company: v })} testid="edit-company" />
          <Input label="CONTACT NAME" value={row.name || ""} onChange={v => setRow({ ...row, name: v })} testid="edit-name" />
          <Input label="EMAIL" value={row.email || ""} onChange={v => setRow({ ...row, email: v })} testid="edit-email" />
          <Input label="ROLE / TITLE" value={row.role || ""} onChange={v => setRow({ ...row, role: v })} testid="edit-role" />
          <SelectInput label="STAGE" value={row.stage} onChange={v => setRow({ ...row, stage: v })} options={STAGES} testid="edit-stage" />
          <SelectInput label="SOURCE" value={row.source || "manual"} onChange={v => setRow({ ...row, source: v })}
            options={["manual", "industrial_waitlist", "founder_guide", "industry_lead", "roi_calculator", "signup", "referral", "outbound", "inbound"]}
            testid="edit-source" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="$ / MONTH (MRR)" type="number" value={row.value_usd_per_month || ""} onChange={v => setRow({ ...row, value_usd_per_month: v })} testid="edit-mrr" />
            <Input label="$ ONE-OFF" type="number" value={row.value_one_off_usd || ""} onChange={v => setRow({ ...row, value_one_off_usd: v })} testid="edit-oneoff" />
          </div>
          <Input label="NEXT ACTION" value={row.next_action || ""} onChange={v => setRow({ ...row, next_action: v })}
            placeholder="e.g. Email decision-maker · Send pilot SOW · Demo Tuesday 3pm" testid="edit-next-action" />
          <Input label="NEXT ACTION DATE" type="date" value={row.next_action_at?.slice(0, 10) || ""} onChange={v => setRow({ ...row, next_action_at: v })} testid="edit-next-date" />
          <Textarea label="NOTES" value={row.notes || ""} onChange={v => setRow({ ...row, notes: v })} rows={4}
            placeholder="Pain points · objections · what they need to commit · etc." testid="edit-notes" />
          {row.stage === "LOST" && (
            <Input label="LOST REASON" value={row.lost_reason || ""} onChange={v => setRow({ ...row, lost_reason: v })}
              placeholder="e.g. Went with competitor · No budget · Wrong fit" testid="edit-lost-reason" />
          )}
        </div>
        <div className="border-t border-zinc-900 p-4 flex items-center justify-between gap-3 sticky bottom-0 bg-black">
          {onDelete && (
            <button onClick={onDelete} disabled={busy} className="border border-[#FF6B47]/40 text-[#FF6B47] hover:bg-[#FF6B47]/10 px-3 py-2 font-mono text-[10px] uppercase tracking-widest inline-flex items-center gap-2" data-testid="sales-edit-delete">
              <Trash2 className="w-3 h-3" /> DELETE
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} disabled={busy} className="border border-zinc-800 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400">CANCEL</button>
            <button onClick={onSave} disabled={busy} data-testid="sales-edit-save"
              className="bg-[#FFB000] text-black font-bold font-mono text-[10px] uppercase tracking-widest px-4 py-2 hover:opacity-90 inline-flex items-center gap-2 disabled:opacity-50">
              <Save className="w-3 h-3" /> {busy ? "SAVING…" : "SAVE"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      />
    </label>
  );
}

function SelectInput({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      >
        {options.map(o => (<option key={o} value={o}>{o.replace("_", " ")}</option>))}
      </select>
    </label>
  );
}

function Textarea({ label, value, onChange, rows = 3, placeholder, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000] resize-none"
      />
    </label>
  );
}
